import { useEffect } from "react";

const DEFAULTS = {
  siteName: "tobiasdeamon · Vault",
  description:
    "A curated archive of every video from the tobiasdeamon YouTube channel — PC benchmarks, mobile gaming marathons, and word-game deep-cuts, catalogued and searchable.",
  image: `${window.location.origin}/og-image.jpg`,
};

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs.query || {})) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs.set || {})) el.setAttribute(k, v);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Set per-page SEO tags: <title>, meta description, canonical, OpenGraph, Twitter.
 * Client-side rendering is fine for Googlebot (it runs JS) and social scrapers
 * that read after JS execution.
 */
export default function usePageMeta({ title, description, image, type = "website" } = {}) {
  useEffect(() => {
    const finalTitle = title && title.includes("tobiasdeamon")
      ? title
      : title
      ? `${title} · ${DEFAULTS.siteName}`
      : DEFAULTS.siteName;
    const finalDesc = description || DEFAULTS.description;
    const finalImg = image || DEFAULTS.image;
    const url = typeof window !== "undefined" ? window.location.href : "";

    document.title = finalTitle;
    upsertMeta('meta[name="description"]', {
      query: { name: "description" },
      set: { name: "description", content: finalDesc },
    });
    upsertLink("canonical", url);

    upsertMeta('meta[property="og:title"]', {
      query: { property: "og:title" },
      set: { property: "og:title", content: finalTitle },
    });
    upsertMeta('meta[property="og:description"]', {
      query: { property: "og:description" },
      set: { property: "og:description", content: finalDesc },
    });
    upsertMeta('meta[property="og:image"]', {
      query: { property: "og:image" },
      set: { property: "og:image", content: finalImg },
    });
    upsertMeta('meta[property="og:type"]', {
      query: { property: "og:type" },
      set: { property: "og:type", content: type },
    });
    upsertMeta('meta[property="og:url"]', {
      query: { property: "og:url" },
      set: { property: "og:url", content: url },
    });
    upsertMeta('meta[property="og:site_name"]', {
      query: { property: "og:site_name" },
      set: { property: "og:site_name", content: DEFAULTS.siteName },
    });

    upsertMeta('meta[name="twitter:card"]', {
      query: { name: "twitter:card" },
      set: { name: "twitter:card", content: "summary_large_image" },
    });
    upsertMeta('meta[name="twitter:title"]', {
      query: { name: "twitter:title" },
      set: { name: "twitter:title", content: finalTitle },
    });
    upsertMeta('meta[name="twitter:description"]', {
      query: { name: "twitter:description" },
      set: { name: "twitter:description", content: finalDesc },
    });
    upsertMeta('meta[name="twitter:image"]', {
      query: { name: "twitter:image" },
      set: { name: "twitter:image", content: finalImg },
    });
  }, [title, description, image, type]);
}
